// SophiaLiveBadge.jsx — a small pill, for the owner/admins only, that appears
// while Sophia (AI voice) is on a call. Click it to listen live (owner request
// 2026-11-02). Listen-only: the call audio is forked by Twilio to the
// voice-live function and arrives here over a Supabase Realtime broadcast.
import { useEffect, useRef, useState } from "react";
import { supabase } from "./supabase";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || "https://fuoejykgpnfphrvqjuub.supabase.co";
const C = { bg: "#1a1a2e", fg: "#ffffff", gold: "#d3a588", green: "#2ecc71", muted: "rgba(255,255,255,0.65)", red: "#e74c3c" };
const F = "'Montserrat',sans-serif";

// G.711 mu-law byte -> float sample
const ULAW = (() => {
  const t = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff, sign = u & 0x80, exp = (u >> 4) & 7, man = u & 0x0f;
    let x = ((man << 3) + 0x84) << exp; x -= 0x84;
    t[i] = (sign ? -x : x) / 32768;
  }
  return t;
})();
function decodeFrames(frames) {
  const parts = frames.map((b64) => { const s = atob(b64); const out = new Float32Array(s.length); for (let i = 0; i < s.length; i++) out[i] = ULAW[s.charCodeAt(i)]; return out; });
  const n = parts.reduce((a, p) => a + p.length, 0); const all = new Float32Array(n);
  let o = 0; for (const p of parts) { all.set(p, o); o += p.length; }
  return all;
}

export default function SophiaLiveBadge({ profile }) {
  const role = String(profile?.role || "").trim().toLowerCase();
  const allowed = ["admin", "owner", "director"].includes(role) || profile?.can_view_sales;
  const [live, setLive] = useState([]);
  const [listening, setListening] = useState(null); // call_sid
  const [status, setStatus] = useState("");
  const ctxRef = useRef(null), nextRef = useRef(0), chanRef = useRef(null);

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    // Only calls someone actually answered (ringing calls have no audio).
    const load = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch(`${SUPABASE_URL}/functions/v1/voice-caller?live=1`, { method: "POST", headers: { Authorization: `Bearer ${session?.access_token || ""}` } });
        const j = await r.json();
        if (alive) setLive(j.calls || []);
      } catch (_) { /* try again next tick */ }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [allowed]);

  // stop when the call we're listening to is gone
  useEffect(() => { if (listening && !live.some((c) => c.call_sid === listening)) stop(); }, [live]); // eslint-disable-line

  const stop = () => {
    if (chanRef.current) { supabase.removeChannel(chanRef.current); chanRef.current = null; }
    if (ctxRef.current) { ctxRef.current.close().catch(() => {}); ctxRef.current = null; }
    setListening(null); setStatus("");
  };

  const listen = async (call) => {
    stop();
    setListening(call.call_sid); setStatus("Connecting…");
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume?.(); // Safari/iPad start suspended until resumed inside the click
    ctxRef.current = ctx; nextRef.current = 0;
    const ch = supabase.channel(`voice-live-${call.call_sid}`).on("broadcast", { event: "audio" }, ({ payload }) => {
      if (!ctxRef.current) return;
      const a = decodeFrames(payload.in || []), b = decodeFrames(payload.out || []);
      const n = Math.max(a.length, b.length);
      if (n) {
        const buf = ctx.createBuffer(1, n, 8000), d = buf.getChannelData(0);
        for (let i = 0; i < n; i++) d[i] = Math.max(-1, Math.min(1, (a[i] || 0) + (b[i] || 0)));
        const src = ctx.createBufferSource(); src.buffer = buf; src.connect(ctx.destination);
        const at = Math.max(ctx.currentTime + 0.25, nextRef.current);
        src.start(at); nextRef.current = at + buf.duration;
        setStatus("Listening");
      }
      if (payload.end) { setStatus("Call ended"); setTimeout(stop, 1500); }
    }).subscribe();
    chanRef.current = ch;
    const { data: { session } } = await supabase.auth.getSession();
    const r = await fetch(`${SUPABASE_URL}/functions/v1/voice-caller?listen=${encodeURIComponent(call.call_sid)}`, { method: "POST", headers: { Authorization: `Bearer ${session?.access_token || ""}` } });
    const j = await r.json().catch(() => ({}));
    if (!r.ok || !j.ok) { setStatus(`Couldn't start: ${j.error || r.status}`); setTimeout(stop, 4000); }
  };

  if (!allowed || !live.length) return null;
  const pill = { position: "fixed", left: "max(16px, env(safe-area-inset-left, 0px))", bottom: "calc(16px + env(safe-area-inset-bottom, 0px))", zIndex: 4500, background: C.bg, color: C.fg, borderRadius: 999, padding: "8px 10px 8px 14px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", fontFamily: F, fontSize: 12.5, maxWidth: "calc(100vw - 32px)" };
  return (
    <div style={{ position: "fixed", left: "max(16px, env(safe-area-inset-left, 0px))", bottom: "calc(16px + env(safe-area-inset-bottom, 0px))", zIndex: 4500, display: "flex", flexDirection: "column", gap: 6, maxWidth: "calc(100vw - 32px)" }}>
      {live.map((c) => {
        const isOn = listening === c.call_sid;
        return (
          <div key={c.call_sid} style={{ ...pill, position: "static" }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: C.green, boxShadow: "0 0 0 4px rgba(46,204,113,0.25)", flex: "none" }} />
            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              Sophia {c.direction === "inbound" ? "is talking to" : "is on a call with"} {c.first_name || "a lead"}
              {isOn && status ? <span style={{ color: C.muted }}> · {status}</span> : null}
            </span>
            <button type="button" onClick={() => (isOn ? stop() : listen(c))}
              style={{ border: 0, borderRadius: 999, padding: "6px 12px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: F, background: isOn ? C.red : C.gold, color: "#1a1a2e", flex: "none" }}>
              {isOn ? "Stop" : "🎧 Listen"}
            </button>
          </div>
        );
      })}
    </div>
  );
}
